import {
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  ScrollArea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconCalendar, IconCreditCard, IconLock, IconPhone, IconUser } from "@tabler/icons-react";
import { useContext, useState } from "react";
import { Colors, FlutterwaveLogoSmall, HttpSuccessCodesObj } from "../../Lib/data";
import { PlanProps, TransactionProps } from "../../Lib/types";
import { AppContext } from "../Dashboard";
import "./styles.scss";
import { PerformRequest } from "../../Lib/usePerformRequest";
import { FlutterwaveInitiatePaymentResponse, NonPaginatedResponse } from "../../Lib/responses";
import { Endpoints } from "../../Lib/endpoints";
import { useDisclosure } from "@mantine/hooks";
import { DefaultSuccessNotification, DefaultErrorNotification } from "../../Lib/methods";
import { useNavigate } from "react-router-dom";
import { AllFatherContext } from "../../App";

const validateCardNumber = (value: string) => {
  const sanitized = value.replace(/\s+/g, "");
  if (!/^[0-9]{16}$/.test(sanitized)) return "Card number must be 16 digits";
  return null;
};

const validateCVV = (value: string) => {
  if (!/^[0-9]{3,4}$/.test(value)) return "CVV must be 3 or 4 digits";
  return null;
};

interface FlutterwaveModalProps {
  opened: boolean;
  onClose: () => void;
  planDetails?: PlanProps;
  customerInfo?: {
    name: string;
    email: string;
    phone?: string;
  };
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const FlutterwaveModal = ({
  opened,
  onClose,
  planDetails,
  customerInfo,
  onSuccess,
  onError,
}: FlutterwaveModalProps) => {
  const [isLoading, setLoading] = useState(false);

  const navigate = useNavigate();

  const validateExpiryMonth = (value: string) => {
    const month = parseInt(value);
    if (!/^\d{1,2}$/.test(value)) return "Enter a valid month (1-12)";
    if (month < 1 || month > 12) return "Month must be between 1 and 12";
    return null;
  };

  const validateExpiryYear = (value: string) => {
    const currentYear = new Date().getFullYear() % 100; // Get last 2 digits of year
    const year = parseInt(value);
    if (!/^\d{2}$/.test(value)) return "Enter a valid 2-digit year";
    if (year < currentYear) return "Card has expired";
    if (year > currentYear + 10) return "Year too far in the future";
    return null;
  };
  const cardForm = useForm({
    initialValues: {
      cardNumber: "",
      cardName: "",
      expiryMonth: "",
      expiryYear: "",
      phoneNumber: "",
      cvv: "",
    },
    validate: {
      cardNumber: validateCardNumber,
      cardName: (v) => (v && v.trim().length > 2 ? null : "Enter cardholder name"),

      expiryMonth: validateExpiryMonth,
      expiryYear: validateExpiryYear,
      cvv: validateCVV,
      phoneNumber: (v) => (v && v.trim().length >= 7 ? null : "Enter a valid phone number"),
    },
  });

  const codeForm = useForm({
    initialValues: {
      code: "",
    },
    validate: {
      code: (v) => (v && v.trim().length < 4 ? "Enter a valid code" : null),
    },
  });

  const context = useContext(AppContext);
  const allFatherContext = useContext(AllFatherContext);
  const handleClose = () => {
    onClose();
  };

  const [reference, setReference] = useState<string | undefined>(undefined);
  const [transaction, setTransaction] = useState<TransactionProps>();
  const [isCodeModalOpen, { open: OpenCodeModal, close: CloseCodeModal }] = useDisclosure(false);
  // INITIATE PAYMENT MIGHT ALSO FINALIZE PAYMENT IF NO FURTHER AUTHORIZATION FROM THE CARD ISSUER IS REQUIRED
  const InitiatePayment = async () => {
    const { hasErrors } = cardForm.validate();
    if (!hasErrors && planDetails && planDetails.id) {
      try {
        setLoading(true);
        const { cardNumber, cardName, expiryMonth, expiryYear, cvv, phoneNumber } = cardForm.values;
        const d = {
          card_number: cardNumber.replace(/\s/g, ""),
          expiry_month: expiryMonth,
          expiry_year: expiryYear,
          cvv,
          card_holder_name: cardName,
          phone_number: phoneNumber,
          currency:
            allFatherContext?.locationInformation?.currency ??
            localStorage.getItem("currency") ??
            "USD",
          planID: planDetails?.id,
        };
        const r = await PerformRequest<NonPaginatedResponse<FlutterwaveInitiatePaymentResponse>>({
          method: Endpoints.GetSubscribeWithFlutterwaveV4().method,
          route: Endpoints.GetSubscribeWithFlutterwaveV4().route,
          data: d,
          callback: () => {
            setLoading(false);
          },
        });
        setLoading(false);
        if (r) {
          if (r.status === HttpSuccessCodesObj.Success) {
            setReference(r?.data?.data?.transaction?.flwReference);
            setTransaction(r?.data?.data?.transaction);
            // SUCCESSFUL BUT AUTHORIZATION REQUIRED (OTP SENT TO PHONE)
            OpenCodeModal();
          } else if (r.status === HttpSuccessCodesObj.Created) {
            // SUCCESSFUL AND AUTHORIZATION NOT REQUIRED
            DefaultSuccessNotification("Payment successful");
            navigate("/d/courses");
          } else {
            // Payment failed lmaoo 🤣🤣🤣🤣
            DefaultErrorNotification("Payment failed");
          }
        }
      } catch (error) {}
    }
  };

  const [isValidating, setValidating] = useState(false);
  const ValidateCode = async () => {
    const { hasErrors } = codeForm.validate();
    if (!hasErrors && planDetails && planDetails.id) {
      try {
        setValidating(true);
        const { code } = codeForm.values;
        const r = await PerformRequest<NonPaginatedResponse<FlutterwaveInitiatePaymentResponse>>({
          method: Endpoints.GetValidateCodeWithFlutterwaveV3(transaction?.id as string).method,
          route: Endpoints.GetValidateCodeWithFlutterwaveV3(transaction?.id as string).route,
          data: { code, reference },
          callback: () => {
            setValidating(false);
          },
        });
        setValidating(false);
        if (r) {
          if (r.status === HttpSuccessCodesObj.Success) {
            // Payment successful
            DefaultSuccessNotification("Payment successful");
            navigate("/d/courses");
          } else {
            // Payment failed lmaoo 🤣🤣🤣🤣
            DefaultErrorNotification("Payment failed");
          }
        }
      } catch (error) {}
    }
  };

  return (
    <>
      {context && context.profile ? (
        <>
          <Modal
            opened={opened}
            onClose={isLoading ? () => {} : handleClose}
            size="lg"
            centered
            withCloseButton={false}
            className="stripe-payment-modal"
            overlayProps={{
              blur: 2,
              opacity: 0.6,
            }}
          >
            <div className="flutterwave-form-container">
              <Paper radius={24} p={32} shadow="xl" className="form">
                <ScrollArea h={450}>
                  <Center>
                    <img className="flutterwave-logo" src={FlutterwaveLogoSmall} alt="" />
                  </Center>
                  <Text c="dimmed" size="sm" ta="center" mb={15}>
                    Checkout With Flutterwave
                  </Text>

                  <Stack gap={18}>
                    <TextInput
                      label="Card Number"
                      placeholder="1234 5678 9012 3456"
                      leftSection={<IconCreditCard size={18} />}
                      maxLength={19}
                      {...cardForm.getInputProps("cardNumber")}
                      onChange={(e) => {
                        // Format as 1234 5678 9012 3456
                        let v = e.target.value.replace(/\D/g, "").slice(0, 16);
                        v = v.replace(/(.{4})/g, "$1 ").trim();
                        cardForm.setFieldValue("cardNumber", v);
                      }}
                      inputMode="numeric"
                      required
                    />
                    <TextInput
                      label="Cardholder Name"
                      placeholder="Name on card"
                      leftSection={<IconUser size={18} />}
                      {...cardForm.getInputProps("cardName")}
                      required
                    />
                    <TextInput
                      label="Phone Number"
                      placeholder="Phone Number"
                      leftSection={<IconPhone size={18} />}
                      {...cardForm.getInputProps("phoneNumber")}
                    />
                    <Group grow>
                      <TextInput
                        label="Expiry Month"
                        placeholder="MM"
                        leftSection={<IconCalendar size={18} />}
                        maxLength={2}
                        {...cardForm.getInputProps("expiryMonth")}
                        onChange={(e) => {
                          // Format as MM/YY
                          let v = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                          cardForm.setFieldValue("expiryMonth", v);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && parseInt(value) < 10 && value.length === 1) {
                            // Add leading zero for single digits
                            cardForm.setFieldValue("expiryMonth", `0${value}`);
                          }
                          // Validate month range on blur
                          if (value && parseInt(value) > 12) {
                            cardForm.setFieldError("expiryMonth", "Month must be between 1 and 12");
                          }
                        }}
                        inputMode="numeric"
                        required
                      />
                      <TextInput
                        label="Expiry Year"
                        placeholder="YY"
                        leftSection={<IconCalendar size={18} />}
                        maxLength={2}
                        {...cardForm.getInputProps("expiryYear")}
                        onChange={(e) => {
                          // Format as MM/YY
                          let v = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                          cardForm.setFieldValue("expiryYear", v);
                        }}
                        inputMode="numeric"
                        required
                      />
                      <PasswordInput
                        label="CVV"
                        placeholder="123"
                        leftSection={<IconLock size={18} />}
                        maxLength={4}
                        {...cardForm.getInputProps("cvv")}
                        inputMode="numeric"
                        required
                      />
                    </Group>
                  </Stack>
                </ScrollArea>
                <Button
                  type="submit"
                  size="md"
                  radius="md"
                  fullWidth
                  className="pay-now-btn"
                  loading={isLoading}
                  onClick={InitiatePayment}
                  disabled={isLoading}
                  mt={8}
                >
                  {isLoading ? <Loader size={20} color="white" /> : "Pay Now"}
                </Button>
              </Paper>
            </div>
          </Modal>
          <Modal
            opened={isCodeModalOpen}
            onClose={isValidating ? () => {} : CloseCodeModal}
            withCloseButton={false}
            size="sm"
            centered
          >
            <TextInput label="Code" placeholder="Enter Code" {...codeForm.getInputProps("code")} />
            <Button
              color={Colors.PurplePrimary}
              fullWidth
              mt={20}
              loading={isValidating}
              onClick={ValidateCode}
            >
              {isValidating ? <Loader size={20} color="white" /> : "Confirm"}
            </Button>
            <Center>
              <Text size="xs" mt={10}>
                Code has been sent to your phone and email
              </Text>
            </Center>
          </Modal>
        </>
      ) : (
        <div>
          <Text>Please login to continue</Text>
        </div>
      )}
    </>
  );
};

export default FlutterwaveModal;
